import { Skeleton, Grid } from '@mui/material';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box } from '@mui/system';
import { Auth } from 'aws-amplify';
import jwtDecode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import { UserPersonStatus } from '../common/enums/enums';
import { DealerRoutes } from '../common/Routes';
import AppBarNav from '../containers/global/AppBarNav';
import AppBarResposiveBox from '../containers/global/AppBarResposiveBox';
import AppMenuDrawer from '../containers/global/AppMenuDrawer';
import { initUserSession } from '../redux/actions/userManagementActions';
import {
  selectUserData,
  selectIsLoading,
  selectUserRoles,
  selectUserPersonStatus,
  selectIsInLogin,
} from '../redux/selectors/userDataSelectors';
import store from '../redux/store';
import ErrorPage from './errors/ErrorPage';
import AppSignInManager from './global/AppSignInManager';

const PREFIX = 'MainContainer';
const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  skeleton: `${PREFIX}-skeleton`,
  menu: `${PREFIX}-menu`,
  menuSkeleton: `${PREFIX}-menuSkeleton`,
  mainContentSkeleton: `${PREFIX}-maniContentContSkeleton`,
  header: `${PREFIX}-header`,
  headerSkeleton: `${PREFIX}-headerSkeleton`,
  contentS: `${PREFIX}-content`,
  contentSkeleton: `${PREFIX}-contentSkeleton`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },
  [`& .${classes.contentContainer}`]: {
    width: '100%',
    backgroundColor: grey[100],
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.only('lg')]: {
      width: 'calc(100% - 64px)',
    },
    [theme.breakpoints.only('xl')]: {
      width: 'calc(100% - 240px)',
    },
  },
  [`& .${classes.content}`]: {
    flexGrow: 1,
    backgroundColor: grey[100],
    padding: '0px',
    position: 'relative',
  },
}));

const SkeletonGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.skeleton}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    height: '100vh',
  },
  [`& .${classes.menu}`]: {
    height: '100%',
    paddingRight: theme.spacing(1),
    [theme.breakpoints.only('lg')]: {
      width: '64px',
    },
    [theme.breakpoints.only('xl')]: {
      width: '240px',
    },
  },
  [`& .${classes.menuSkeleton}`]: {
    transformOrigin: '0 0',
    transform: 'scale(1)',
    height: 'calc(90vh - 24px)',
  },
  [`& .${classes.mainContentSkeleton}`]: {
    flexDirection: 'row',
    width: '100%',
  },
  [`& .${classes.header}`]: {
    width: '100%',
  },
  [`& .${classes.headerSkeleton}`]: {
    width: '100%',
    height: '10vh',
    transformOrigin: '0 0',
    transform: 'scale(1)',
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.contentS}`]: {
    flexGrow: 1,
    [theme.breakpoints.only('lg')]: {
      width: 'calc(100% - 64px)',
    },
    [theme.breakpoints.only('xl')]: {
      width: 'calc(100% - 240px)',
    },
  },
  [`& .${classes.contentSkeleton}`]: {
    width: '100%',
    height: 'calc(90vh - 24px)',
    transformOrigin: '0 0',
    transform: 'scale(1)',
  },
}));

const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

const MainRouter = ({ userData, handleOpen, open, componentRoutes }) => {
  const isLg = useMediaQuery(theme => theme.breakpoints.up('lg'));
  return (
    (userData && (
      <Router>
        <Root className={classes.root}>
          <AppBarNav handleOpen={handleOpen} />

          <Box>
            {isLg && <AppMenuDrawer variant="permanent" open={true} />}
            <AppMenuDrawer
              variant="temporary"
              open={open}
              onClose={handleOpen}
            />
          </Box>

          <div className={classes.contentContainer}>
            <Offset />
            <AppBarResposiveBox />
            <main className={classes.content}>
              <Switch>{componentRoutes}</Switch>
            </main>
          </div>
        </Root>
      </Router>
    )) || <ErrorPage />
  );
};

const LoadingState = () => {
  return (
    <SkeletonGrid container className={classes.skeleton}>
      <Grid item className={classes.mainContentSkeleton}>
        <Grid container style={{ width: '100%' }}>
          <Grid item className={classes.header}>
            <Skeleton className={classes.headerSkeleton} />
          </Grid>
          <Grid item className={classes.menu}>
            <Skeleton className={classes.menuSkeleton} />
          </Grid>
          <Grid item className={classes.contentS}>
            <Skeleton className={classes.contentSkeleton} />
          </Grid>
        </Grid>
      </Grid>
    </SkeletonGrid>
  );
};

const InitStatesHandler = (
  loading,
  userData,
  userPersonStatus,
  handleOpen,
  open,
  componentRoutes
) => {
  const isInLogin = useSelector(selectIsInLogin);

  if (loading) return <LoadingState />;
  if (
    userPersonStatus === UserPersonStatus.PENDING ||
    (userPersonStatus === UserPersonStatus.ACTIVE && isInLogin)
  )
    return <AppSignInManager />;
  return (
    <MainRouter
      userData={userData}
      handleOpen={handleOpen}
      open={open}
      componentRoutes={componentRoutes}
    />
  );
};

const getUserInfo = async currentUserData => {
  await Auth.currentAuthenticatedUser()
    .then(userData => {
      if (currentUserData) return;
      const userDataDecode = jwtDecode(
        userData.signInUserSession.idToken.jwtToken
      );
      store.dispatch(initUserSession(userDataDecode));
    })
    .catch(() => {
      /* do nothing for now */
    });
};

const MainContainer = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(!open);
  };

  const userData = useSelector(selectUserData);
  const userPersonStatus = useSelector(selectUserPersonStatus);
  const userRoles = useSelector(selectUserRoles);
  const routes = DealerRoutes(userRoles);
  const loading = useSelector(selectIsLoading);

  useEffect(() => {
    getUserInfo(userData);
  }, [userData]);

  const componentRoutes = routes.map(({ path, component, redirect }, key) =>
    !redirect ? (
      <Route exact path={path} component={component} key={key} />
    ) : (
      <Route exact path={path} key={key}>
        <Redirect to={redirect} />
      </Route>
    )
  );

  return InitStatesHandler(
    loading,
    userData,
    userPersonStatus,
    handleOpen,
    open,
    componentRoutes
  );
};

export default MainContainer;
